<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{
  onAnimationComplete: () => void
}>()

const visible = ref(true)
const isHidden = ref(false)
const selectedAnimation = ref(1)

onMounted(() => {
  // Randomly select animation pattern (1-4)
  selectedAnimation.value = 1;
  
  // Wait for animation to complete before fading out
  setTimeout(() => {
    isHidden.value = true
    // Wait for fade out transition to complete
    setTimeout(() => {
      visible.value = false
      props.onAnimationComplete()
    }, 300) // Same as transition duration
  }, 2000)
})
</script>

<template>
  <Transition>
    <div v-if="visible" 
         class="boot-screen"
         :class="{ 'fade-out': isHidden }">
      <!-- Stellar background with stars -->
      <div class="stars"></div>
      <div class="stars2"></div>
      <div class="stars3"></div>

      <!-- Brand icon -->
      <div class="brand-icon-container"
           :class="'animation-pattern-' + selectedAnimation">
        <img 
          src="@/assets/logos/brand_icon_element_color_webp.webp" 
          alt="ALTERLAN" 
          class="brand-icon"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.boot-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #414a4a;
  z-index: 9999;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.fade-out {
  opacity: 0;
}

.brand-icon-container {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  transform: translate(-50%, 200%);
  will-change: transform;
  opacity: 0;
}

.brand-icon {
  width: 150px;
  height: auto;
}

/* Pattern 1: Smooth sine wave */
.animation-pattern-1 {
  animation: fly-through-1 2s ease-in-out forwards;
}

/* Pattern 1: Smooth sine wave */
@keyframes fly-through-1 {
  0% { transform: translate(-50%, 200%); opacity: 0; }
  20% { transform: translate(-50%, -40%); opacity: 1; }
  25% { transform: translate(-50%, -43%); opacity: 1; }
  30% { transform: translate(-50%, -45%); opacity: 1; }
  35% { transform: translate(-50%, -47%); opacity: 1; }
  40% { transform: translate(-50%, -48%); opacity: 1; }
  45% { transform: translate(-50%, -47%); opacity: 1; }
  50% { transform: translate(-50%, -45%); opacity: 1; }
  55% { transform: translate(-50%, -43%); opacity: 1; }
  60% { transform: translate(-50%, -45%); opacity: 1; }
  65% { transform: translate(-50%, -47%); opacity: 1; }
  70% { transform: translate(-50%, -50%); opacity: 1; }
  100% { transform: translate(-50%, -200%); opacity: 0; }
}

/* Star animations */
.stars, .stars2, .stars3 {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: transparent;
}

.stars {
  background: radial-gradient(2px 2px at 20px 30px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(2px 2px at 40px 70px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(2px 2px at 50px 160px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(2px 2px at 90px 40px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(2px 2px at 130px 80px, #76c4ae, rgba(0,0,0,0));
  background-size: 200px 200px;
  animation: stars-animation 4s linear infinite;
}

.stars2 {
  background: radial-gradient(1px 1px at 100px 50px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(1px 1px at 150px 90px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(1px 1px at 70px 120px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(1px 1px at 180px 60px, #76c4ae, rgba(0,0,0,0));
  background-size: 300px 300px;
  animation: stars-animation 6s linear infinite;
}

.stars3 {
  background: radial-gradient(1px 1px at 200px 100px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(1px 1px at 250px 180px, #76c4ae, rgba(0,0,0,0)),
              radial-gradient(1px 1px at 170px 220px, #76c4ae, rgba(0,0,0,0));
  background-size: 400px 400px;
  animation: stars-animation 8s linear infinite;
}

@keyframes stars-animation {
  from { transform: translateX(0); }
  to { transform: translateX(-200px); }
}

/* Transition animations */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
